/* eslint-disable no-bitwise, style/max-statements-per-line */
/// <summary>
/// Take the signed value representing the color and generate the argb format to use as inline style

import type { Color } from "culori/fn";
import { formatHex8 } from "culori/fn";

/// </summary>
export function toInlineArgb(val: number) {
  return `rgba(${((val >> 16) & 0xFF)}, ${((val >> 8) & 0xFF)}, ${val & 0xFF}, ${((val >> 24) & 0xFF) / 255})`;
}

export function toInlineArgbWithAlpha(val: number, alpha: number) {
  return `rgba(${((val >> 16) & 0xFF)}, ${((val >> 8) & 0xFF)}, ${val & 0xFF}, ${alpha})`;
}

export function getArgb(val: number | undefined) {
  if (val === undefined) return null;

  return {
    a: ((val >> 24) & 0xFF),
    r: ((val >> 16) & 0xFF),
    g: ((val >> 8) & 0xFF),
    b: val & 0xFF,
  };
}

export function fromArgb(a: number, r: number, g: number, b: number): number {
  return a << 24 | r << 16 | g << 8 | b;
}

/// <summary>
/// Generate the int representation of the color from rgba()/rgb()
/// </summary>
export function fromInlineArgb(val?: string) {
  // zero shift values into their position and then bitwise | them together to produce the resulting double val
  if (!val) return undefined;
  const compString = val.substring(val.indexOf("(") + 1, val.indexOf(")"));
  const comps = compString.split(",");

  const r = Number(comps[0]);
  const g = Number(comps[1]);
  const b = Number(comps[2]);
  const a = val.startsWith("rgba(") ? 255 * Number(comps[3]) : 255;

  return a << 24 | r << 16 | g << 8 | b;
}

/// <summary>
/// From the signed integer form, convert the rgba value into a simple RGB hex
/// </summary>
export function toHex(val: number, includePound: boolean = true): string {
  return `${includePound ? "#" : ""}${(((val >> 16) & 0xFF)).toString(16).padStart(2, "0")}${((val >> 8) & 0xFF).toString(16).padStart(2, "0")}${(val & 0xFF).toString(16).padStart(2, "0")}`;
}

/// <summary>
/// From hex RGB, convert into signed integer form
/// </summary>
export function fromHex(val: string): number {
  const hexValue = val.trim().replace(/#+/g, "");
  const isFull = hexValue.length === 6;

  const r = Number.parseInt(isFull ? hexValue.substring(0, 2) : hexValue.substring(0, 1), 16);
  const g = Number.parseInt(isFull ? hexValue.substring(2, 4) : hexValue.substring(1, 2), 16);
  const b = Number.parseInt(isFull ? hexValue.substring(4, 6) : hexValue.substring(2, 3), 16);
  const a = 255;

  return fromArgb(a, r, g, b);
}

export function fromSignedArgbToHex(value: number) {
  const argb = getArgb(value);
  if (argb == null) {
    throw new Error("Failed to convert color: invalid input");
  }

  return formatHex8({ mode: "rgb", r: argb.r / 255, g: argb.g / 255, b: argb.b / 255, alpha: argb.a / 255 });
}

export function fromSignedArgbToCulori(value: number): Color {
  const argb = getArgb(value);
  if (argb == null) {
    throw new Error("Failed to convert color: invalid input");
  }

  return {
    mode: "rgb",
    r: argb.r / 255,
    g: argb.g / 255,
    b: argb.b / 255,
    alpha: argb.a / 255,
  };
}

export function fromCuloriToSignedArgb(color: Color): number {
  return fromHexToSignedArgb(formatHex8(color));
}

export function fromHexToSignedArgb(hex: string): number {
  const clean = hex.replace(/^#/, "");
  if (clean.length === 6) {
    return fromHex6toSignedArgb(clean);
  }

  if (clean.length === 8) {
    return fromHex8toSignedArgb(clean);
  }

  throw new Error(`Invalid hex color: ${hex}`);
}

export function fromHex6toSignedArgb(hex: string): number {
  const clean = hex.replace(/^#/, "");
  if (clean.length !== 6) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  const r = Number.parseInt(clean.slice(0, 2), 16);
  const g = Number.parseInt(clean.slice(2, 4), 16);
  const b = Number.parseInt(clean.slice(4, 6), 16);
  const a = 255;

  return fromArgb(a, r, g, b);
}

export function fromHex8toSignedArgb(hex: string): number {
  const clean = hex.replace(/^#/, "");
  if (clean.length !== 8) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  const r = Number.parseInt(clean.slice(0, 2), 16);
  const g = Number.parseInt(clean.slice(2, 4), 16);
  const b = Number.parseInt(clean.slice(4, 6), 16);
  const a = Number.parseInt(clean.slice(6, 8), 16);

  return fromArgb(a, r, g, b);
}

export function rgbFromHex(val: string | undefined): { r: number; g: number; b: number } {
  if (!val) return { r: 255, g: 0, b: 0 };

  const hexValue = val.trim().replace(/#+/g, "");
  const isFull = hexValue.length === 6;

  const r = Number.parseInt(isFull ? hexValue.substring(0, 2) : hexValue.substring(0, 1), 16);
  const g = Number.parseInt(isFull ? hexValue.substring(2, 4) : hexValue.substring(1, 2), 16);
  const b = Number.parseInt(isFull ? hexValue.substring(4, 6) : hexValue.substring(2, 3), 16);

  return { r, g, b };
}

export function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;

  switch (max) {
    case min: break;
    case r: h = (g - b) + d * (g < b ? 6 : 0); h /= 6 * d; break;
    case g: h = (b - r) + d * 2; h /= 6 * d; break;
    case b: h = (r - g) + d * 4; h /= 6 * d; break;
  }

  return {
    h: h * 360,
    s: (max === 0 ? 0 : d / max),
    v: max / 255,
  };
}

export function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  let r = 0;
  let g = 0;
  let b = 0;
  const i = Math.floor(h / 360 * 6);
  const f = h / 360 * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}
